import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  FaSignOutAlt,
  FaStore,
  FaUsers,
  FaShieldAlt,
  FaExchangeAlt,
  FaClipboardCheck,
  FaDatabase,
  FaLightbulb,
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import NotificationCenter, { Notification } from '../NotificationCenter';

const NAV_ITEMS = [
  { to: '/admin/postos', label: 'Postos', icon: FaStore },
  { to: '/admin/solicitacoes', label: 'Solicitações', icon: FaClipboardCheck },
  { to: '/admin/oportunidades', label: 'Oportunidades', icon: FaLightbulb },
  { to: '/admin/anp', label: 'ANP', icon: FaDatabase },
  { to: '/admin/usuarios', label: 'Usuários', icon: FaUsers },
];

const AdminHeader: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Simular notificações (substituir por chamada real da API)
  useEffect(() => {
    // Carregar notificações salvas do localStorage
    const savedNotifications = localStorage.getItem('adminNotifications');
    if (savedNotifications) {
      setNotifications(JSON.parse(savedNotifications));
    }
  }, []);

  // Salvar notificações no localStorage
  useEffect(() => {
    localStorage.setItem('adminNotifications', JSON.stringify(notifications));
  }, [notifications]);

  const handleMarkAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleClearNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleLogout = async () => {
    await logout();
  };

  const switchToPartnerView = () => {
    navigate('/postos');
  };

  return (
    <header className="bg-gradient-to-br from-red-900 to-red-800 text-white px-4 shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto flex justify-between items-center h-16">
        {/* Left Section */}
        <div className="flex items-center gap-6">
          {/* Brand */}
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="AbasteceMais" className="w-8 h-8 object-contain" />
            <h1 className="text-lg font-bold bg-gradient-to-br from-white to-red-100 bg-clip-text text-transparent hidden md:block">
              AbasteceMais
            </h1>
            <span className="flex items-center gap-1 bg-red-500/20 border border-red-400/30 text-red-100 px-2 py-0.5 rounded-full text-[10px] font-medium">
              <FaShieldAlt className="text-[9px]" />
              <span className="hidden xl:inline">Painel</span> Admin
            </span>
          </div>

          {/* Navigation - Desktop */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                    isActive
                      ? 'text-white bg-white/20 border border-white/20'
                      : 'text-red-100/70 hover:text-white hover:bg-white/10'
                  }`
                }
              >
                <Icon className="text-sm" />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          {/* Notification Center */}
          <NotificationCenter
            notifications={notifications}
            onMarkAsRead={handleMarkAsRead}
            onMarkAllAsRead={handleMarkAllAsRead}
            onClear={handleClearNotification}
          />

          {/* Switch to Partner View */}
          <button
            onClick={switchToPartnerView}
            className="hidden md:flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-br from-[#089453] to-[#84cc34] text-[#0f1419] rounded text-xs font-medium transition-all duration-200 hover:shadow-md"
            title="Visualizar como Parceiro"
          >
            <FaExchangeAlt className="text-xs" />
            <span>Painel Parceiro</span>
          </button>

          {/* User Info */}
          <div className="flex items-center gap-2 px-2 py-1 bg-white/10 border border-white/20 rounded-md">
            <div className="w-6 h-6 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
              <FaShieldAlt className="text-white text-[10px]" />
            </div>
            <div className="hidden lg:flex flex-col">
              <span className="text-xs font-semibold leading-tight">{user?.fullName || 'Admin'}</span>
              <span className="flex items-center gap-1 text-[9px] text-red-200 uppercase leading-tight">
                <FaShieldAlt className="text-[8px]" />
                ADMIN
              </span>
            </div>
          </div>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 bg-red-500/20 border border-red-400/30 text-red-100 px-2.5 py-1 rounded text-xs font-medium transition-all duration-200 hover:bg-red-500/30 hover:border-red-300"
            title="Sair"
          >
            <FaSignOutAlt className="text-xs" />
            <span className="hidden sm:inline">Sair</span>
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-gradient-to-br from-red-900 to-red-800 px-4 py-3 flex justify-around border-t border-white/10 shadow-2xl z-50">
        {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all duration-200 ${
                isActive ? 'text-white bg-white/20' : 'text-red-100/70'
              }`
            }
          >
            <Icon className="text-base" />
            <span className="text-[11px]">{label}</span>
          </NavLink>
        ))}
      </nav>
    </header>
  );
};

export default AdminHeader;

